import React, { useState } from "react";
import {
  Box,
  Container,
  HStack,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Text,
  Button,
  useColorModeValue,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { FiMenu, FiSearch, FiHeart, FiUser, FiShoppingCart, FiCpu, FiHardDrive, FiMonitor, FiSettings } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import type { Product } from "../types";

const Header: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const { items: cartItems } = useCart();

  const cartItemsCount = cartItems.reduce(
    (total: number, item: Product) => total + item.quantity,
    0
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  return (
    <Box
      as="header"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      bg={bgColor}
      borderBottom="1px"
      borderColor={borderColor}
      boxShadow="sm"
    >
      <Container maxW="container.xl" py={4}>
        <Flex justify="space-between" align="center">
          {/* Logo and Brand */}
          <HStack spacing={8}>
            <IconButton
              aria-label="Menu"
              icon={<FiMenu />}
              variant="ghost"
              display={{ base: "flex", md: "none" }}
              onClick={onOpen}
            />
            <RouterLink to="/">
              <HStack spacing={2}>
                <Icon as={FiCpu} w={8} h={8} color="green.600" />
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  bgGradient="linear(to-r, #166534, #14532d)"
                  bgClip="text"
                  letterSpacing="tight"
                  _hover={{
                    bgGradient: "linear(to-r, #14532d, #064e3b)",
                    transition: "all 0.3s",
                  }}
                >
                  TechParts Hub
                </Text>
              </HStack>
            </RouterLink>
          </HStack>

          {/* Navigation */}
          <HStack spacing={8} display={{ base: "none", md: "flex" }}>
            <Menu>
              <MenuButton as={Button} variant="ghost" rightIcon={<FiCpu />}>
                Components
              </MenuButton>
              <MenuList>
                <MenuItem as={RouterLink} to="/category/processors" icon={<FiCpu />}>
                  Processors
                </MenuItem>
                <MenuItem as={RouterLink} to="/category/storage" icon={<FiHardDrive />}>
                  Storage
                </MenuItem>
                <MenuItem as={RouterLink} to="/category/graphics-cards" icon={<FiMonitor />}>
                  Graphics Cards
                </MenuItem>
                <MenuItem as={RouterLink} to="/category/peripherals" icon={<FiSettings />}>
                  Peripherals
                </MenuItem>
              </MenuList>
            </Menu>
            <RouterLink to="/sale">
              <Button variant="ghost" colorScheme="green">
                Deals
              </Button>
            </RouterLink>
            <RouterLink to="/about">
              <Button variant="ghost">About</Button>
            </RouterLink>
            <RouterLink to="/contact">
              <Button variant="ghost">Contact</Button>
            </RouterLink>
          </HStack>

          {/* Search and Actions */}
          <HStack spacing={4}>
            <form onSubmit={handleSearch}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search parts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="md"
                  width={{ base: "150px", md: "300px" }}
                  _hover={{ borderColor: "green.400" }}
                  _focus={{ borderColor: "green.400" }}
                />
              </InputGroup>
            </form>
            <RouterLink to="/cart">
              <IconButton
                aria-label="Cart"
                icon={<FiShoppingCart />}
                variant="ghost"
                size="lg"
                position="relative"
              >
                {cartItemsCount > 0 && (
                  <Badge
                    position="absolute"
                    top={-2}
                    right={-2}
                    colorScheme="red"
                    borderRadius="full"
                  >
                    {cartItemsCount}
                  </Badge>
                )}
              </IconButton>
            </RouterLink>
          </HStack>
        </Flex>
      </Container>

      {/* Mobile Menu */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Menu</DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              <Menu>
                <MenuButton as={Button} variant="ghost" rightIcon={<FiCpu />}>
                  Components
                </MenuButton>
                <MenuList>
                  <MenuItem as={RouterLink} to="/category/processors" icon={<FiCpu />}>
                    Processors
                  </MenuItem>
                  <MenuItem as={RouterLink} to="/category/storage" icon={<FiHardDrive />}>
                    Storage
                  </MenuItem>
                  <MenuItem as={RouterLink} to="/category/graphics-cards" icon={<FiMonitor />}>
                    Graphics Cards
                  </MenuItem>
                  <MenuItem as={RouterLink} to="/category/peripherals" icon={<FiSettings />}>
                    Peripherals
                  </MenuItem>
                </MenuList>
              </Menu>
              <RouterLink to="/sale">
                <Button variant="ghost" colorScheme="green" width="100%">
                  Deals
                </Button>
              </RouterLink>
              <RouterLink to="/about">
                <Button variant="ghost" width="100%">
                  About
                </Button>
              </RouterLink>
              <RouterLink to="/contact">
                <Button variant="ghost" width="100%">
                  Contact
                </Button>
              </RouterLink>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;
